'use client';

import { Spinner } from '@/components/glitch-ui/spinner';
import { useState, useMemo, useRef, useEffect, forwardRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useVirtualizer } from '@tanstack/react-virtual';

import { Trash2, Plus, Search, Pencil, AlertCircle, Lock, LockOpen, PauseCircle, ArrowUpDown, ArrowUp, ArrowDown, ChevronDown, ListTodo } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ConformationModal } from '@/components/glitch-ui/confirmation-modal';
import { FieldError, InputAdornment } from '@/components/glitch-ui/field-error';
import { MatchTypeSelect } from './match-type-select';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
} from '@/components/ui/dropdown-menu';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import type { Keyword, CampaignData } from '@/lib/types';
import { StringBadge } from '../../glitch-ui/string-badge';
import { formatKeywordForDisplay } from '@/lib/utils';
import { useTableSorting } from '@/hooks/useTableSorting';
import { SortableHeader } from '../../glitch-ui/data-table/sortable-header';
import { QualityScorePill } from '@/components/glitch-ui/quality-score-pill';
// #4506: keyword detail sidebar disabled — import kept commented for re-enable.
// import { KeywordMetricsSidebar } from './keyword-metrics-sidebar';
import { useBatchKeywordMetrics } from '@/queries/keyword-queries';
import { fetchKeywordPerformanceBatch, KeywordPerformanceData, BatchKeywordMetrics, lockKeyword, pauseKeyword, resumeKeyword, updateKeyword, addKeywords } from '@/lib/apis';
import { useRefetchCampaign } from '@/hooks/useRefetchCampaign';
import { useCurrencySymbol } from '@/hooks/useCurrencySymbol';
import { formatCompactNumber, formatCurrency } from '@/i18n/formatters';
import { toast } from '@/hooks/useToast';
import { useTestMode } from '@/hooks/useTestMode';


export interface KeywordManagerProps {
  keywords: Keyword[];
  setKeywords: React.Dispatch<React.SetStateAction<Keyword[]>>;
  adId?: number;
  alternativeKeywords: Keyword[];
  setAlternativeKeywords: React.Dispatch<React.SetStateAction<Keyword[]>>;
  customKeyword: string;
  setCustomKeyword: React.Dispatch<React.SetStateAction<string>>;
  maxKeywordLength: number;
  disabled?: boolean;
  /** Hide the Add button while the ad is still generating. */
  hideAddButton?: boolean;
  campaign?: CampaignData;
  /**
   * Google Ads ad group ID for the ad currently being viewed. Required to scope
   * keyword performance data to this ad group: the same keyword text can exist
   * in multiple ad groups within one campaign, and the batch endpoint returns
   * rows for all of them.
   */
  googleadsAdGroupId?: string;
  onSaveAd?: (updatedKeywords: Keyword[]) => void;
}

// Pill styling for a keyword match type, shared by the table row badges and the bulk-actions
// "Set match type" menu (#4819) so they read identically.
const getMatchTypePillClasses = (matchType: string | null | undefined) => {
  const mt = (matchType || 'BROAD').toUpperCase();
  if (mt === 'PHRASE') return 'bg-purple-100 text-purple-800';
  if (mt === 'EXACT') return 'bg-blue-100 text-blue-800';
  return 'bg-amber-100 text-amber-800';
};
const matchTypeLabel = (mt: string) => mt.charAt(0) + mt.slice(1).toLowerCase();

// Component for a single keyword row with metrics
const KeywordTableRow = forwardRef<HTMLTableRowElement, {
  keyword: Keyword;
  disabled: boolean;
  onRemove: (keyword: Keyword) => void;
  onEdit: (keyword: Keyword) => void;
  onClick: (keyword: Keyword) => void;
  onLock: (keyword: Keyword, isLocked: boolean) => void;
  onPause: (keyword: Keyword) => void;
  onResume: (keyword: Keyword) => void;
  /** Bulk-edit mode (#4819): reveals a leading selection checkbox and disables row navigation. */
  bulkMode: boolean;
  isSelected: boolean;
  onToggleSelect: (keyword: Keyword) => void;
  metricsData?: BatchKeywordMetrics;
  metricsLoading: boolean;
  performanceData?: KeywordPerformanceData;
  performanceLoading: boolean;
  /** Performance + Bidding columns only render when the campaign has been live at some point. */
  hasBeenLive: boolean;
  /** #5555: change a keyword's match type inline (gated by a confirm step in the parent). */
  onMatchTypeChange?: (keyword: Keyword, matchType: string) => void;
  /** Row index in the virtualized list — feeds the virtualizer's measureElement (#5309). */
  dataIndex?: number;
}>(function KeywordTableRow({
  keyword,
  disabled,
  onRemove,
  onEdit,
  onClick,
  onLock,
  onPause,
  onResume,
  bulkMode,
  isSelected,
  onToggleSelect,
  metricsData,
  metricsLoading,
  performanceData,
  performanceLoading,
  hasBeenLive,
  onMatchTypeChange,
  dataIndex,
}, ref) {
  const hasId = !!keyword.id;
  // #4506: keyword detail sidebar disabled; row clicks do nothing for now.
  // const isClickable = hasId;
  const isClickable = false;
  void hasId;

  const isLoading = metricsLoading || performanceLoading;

  // Extract metrics from batch data
  const reach = metricsData?.avg_monthly_searches;
  const competition = metricsData?.competition;

  // Performance metrics from Google Ads
  const impressions = performanceData?.impressions;
  const clicks = performanceData?.clicks;
  const cost = performanceData?.cost;
  const conversions = performanceData?.conversions;
  const impressionShare = performanceData?.search_impression_share;
  const firstPageCpc = performanceData?.first_page_cpc;
  const firstPositionCpc = performanceData?.first_position_cpc;

  // Performance values (cost, CPCs) come from Google Ads, so use that account's currency.
  const { currency } = useCurrencySymbol('google');

  const formatPercent = (num: number | null | undefined) => {
    if (num === null || num === undefined) return '-';
    return `${Math.round(num)}%`;
  };

  const getCompetitionBadgeClasses = (comp: string | null | undefined) => {
    if (!comp) return 'bg-gray-100 text-gray-800';
    if (comp === 'LOW') return 'bg-green-100 text-green-800';
    if (comp === 'MEDIUM') return 'bg-yellow-100 text-yellow-800';
    return 'bg-red-100 text-red-800';
  };

  return (
    <tr
      ref={ref}
      data-index={dataIndex}
      className={`${(isClickable || bulkMode) ? 'cursor-pointer' : ''} ${isSelected ? 'bg-[#290BD9]/5 hover:bg-[#290BD9]/10' : keyword.is_paused ? 'bg-gray-50 hover:bg-gray-100' : 'hover:bg-gray-50'}`}
      onClick={() => {
        if (bulkMode) { onToggleSelect(keyword); return; }
        if (isClickable) onClick(keyword);
      }}
    >
      {bulkMode && (
        <td className='pl-4 pr-0 w-10 align-middle' onClick={(e) => e.stopPropagation()}>
          <div className='flex h-full items-center'>
            <Checkbox checked={isSelected} onCheckedChange={() => onToggleSelect(keyword)} aria-label={`Select ${keyword.text}`} />
          </div>
        </td>
      )}
      <td className='pl-[26px] pr-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900'>
        <div>{formatKeywordForDisplay(keyword.text)}</div>
        {/* Keyword-level Final URL override, shown only when set. Truncated + width-capped because the
            td is whitespace-nowrap; a long URL would otherwise push the metrics columns off-screen. */}
        {keyword.final_url ? (
          <a
            href={keyword.final_url}
            target='_blank'
            rel='noopener noreferrer'
            onClick={(e) => e.stopPropagation()}
            title={keyword.final_url}
            className='block max-w-[240px] truncate text-xs font-normal text-[#290BD9] hover:text-[#2309C5] hover:underline'
          >
            {keyword.final_url.replace(/^https?:\/\//, '')}
          </a>
        ) : null}
      </td>
      <td className='px-2 py-3 whitespace-nowrap text-center'>
        {onMatchTypeChange ? (
          <MatchTypeSelect
            matchType={keyword.match_type}
            onChange={(mt) => onMatchTypeChange(keyword, mt)}
            pillClasses={getMatchTypePillClasses}
            className='mx-auto'
            disabled={disabled || bulkMode}
          />
        ) : (
          <span className={`inline-flex items-center px-3.5 py-0.5 rounded-full text-xs font-medium ${getMatchTypePillClasses(keyword.match_type)}`}>
            {matchTypeLabel(keyword.match_type || 'BROAD')}
          </span>
        )}
      </td>
      <td className='px-4 py-3 whitespace-nowrap text-sm font-normal text-gray-900 text-center'>
        {isLoading ? (
          <Spinner size="xs" className="text-gray-400 inline" />
        ) : (
          formatCompactNumber(reach)
        )}
      </td>
      <td className='px-4 py-3 whitespace-nowrap text-center'>
        {isLoading ? (
          <Spinner size="xs" className="text-gray-400 inline" />
        ) : competition ? (
          <span className={`inline-flex items-center px-3.5 py-0.5 rounded-full text-xs font-medium ${getCompetitionBadgeClasses(competition)}`}>
            {competition.charAt(0) + competition.slice(1).toLowerCase()}
          </span>
        ) : (
          <span className='text-sm font-normal text-gray-500'>-</span>
        )}
      </td>
      {hasBeenLive && (
        <td className='px-4 py-3 whitespace-nowrap'>
          {isLoading ? (
            <Spinner size="xs" className="text-gray-400 inline" />
          ) : (
            <div className='flex items-center gap-3 text-sm text-gray-900'>
              <span>{formatCompactNumber(impressions)} <span className='text-xs text-gray-500'>imp</span></span>
              <span>{formatCompactNumber(clicks)} <span className='text-xs text-gray-500'>clicks</span></span>
              <span>{formatCurrency(cost, currency)} <span className='text-xs text-gray-500'>cost</span></span>
              <span>{formatCompactNumber(conversions)} <span className='text-xs text-gray-500'>conv</span></span>
            </div>
          )}
        </td>
      )}
      {hasBeenLive && (
        <td className='px-4 py-3 whitespace-nowrap'>
          {isLoading ? (
            <Spinner size="xs" className="text-gray-400 inline" />
          ) : (
            <div className='flex items-center gap-3 text-sm text-gray-900'>
              <span>{impressionShare !== null && impressionShare !== undefined ? formatPercent(impressionShare * 100) : '-'} <span className='text-xs text-gray-500'>share</span></span>
              <span>{formatCurrency(firstPageCpc, currency)} <span className='text-xs text-gray-500'>1st pg</span></span>
              <span>{formatCurrency(firstPositionCpc, currency)} <span className='text-xs text-gray-500'>1st pos</span></span>
            </div>
          )}
        </td>
      )}
      {hasBeenLive && (
        <td className='px-4 py-3 whitespace-nowrap text-center'>
          {isLoading ? (
            <Spinner size="xs" className="text-gray-400 inline" />
          ) : (
            <QualityScorePill score={performanceData?.quality_score} subScores={performanceData} />
          )}
        </td>
      )}
      <td className='px-4 py-3 whitespace-nowrap text-right'>
        {/* #4819: grey out (don't hide) the per-row actions while bulk mode is active — keeps them
            from competing with the Bulk actions menu without collapsing the row height. */}
        {!disabled && (
          <div className={`flex justify-end gap-1 ${bulkMode ? 'pointer-events-none opacity-40' : ''}`}>
            <Button
              variant='ghost'
              size='icon'
              className={`h-8 w-8 hover:bg-[#FEF3C7] ${keyword.is_paused ? 'text-[#A16207] bg-[#FEF3C7] ring-1 ring-inset ring-[#FDE68A]' : 'text-gray-400 hover:text-[#A16207]'}`}
              onClick={(e) => {
                e.stopPropagation();
                keyword.is_paused ? onResume(keyword) : onPause(keyword);
              }}
            >
              <PauseCircle className='h-4 w-4' />
              <span className='sr-only'>{keyword.is_paused ? 'Resume' : 'Pause'} {formatKeywordForDisplay(keyword.text)}</span>
            </Button>
            <Button
              variant='ghost'
              size='icon'
              className={`h-8 w-8 ${keyword.is_locked ? 'text-[#290BD9] bg-[#EDE9FE] hover:bg-[#C4B5FD]' : 'text-gray-400 hover:text-[#290BD9] hover:bg-[#EDE9FE]'}`}
              onClick={(e) => {
                e.stopPropagation();
                onLock(keyword, !keyword.is_locked);
              }}
              disabled={keyword.is_paused}
            >
              {keyword.is_locked ? <Lock className='h-4 w-4' /> : <LockOpen className='h-4 w-4' />}
              <span className='sr-only'>{keyword.is_locked ? 'Unlock' : 'Lock'} {formatKeywordForDisplay(keyword.text)}</span>
            </Button>
            <Button
              variant='ghost'
              size='icon'
              className='h-8 w-8 text-gray-400 hover:text-gray-600 hover:bg-gray-50'
              onClick={(e) => {
                e.stopPropagation();
                onEdit(keyword);
              }}
              disabled={keyword.is_paused}
            >
              <Pencil className='h-4 w-4' />
              <span className='sr-only'>Edit {formatKeywordForDisplay(keyword.text)}</span>
            </Button>
            <Button
              variant='ghost'
              size='icon'
              className='h-8 w-8 text-gray-400 hover:text-red-600 hover:bg-red-50'
              onClick={(e) => {
                e.stopPropagation();
                onRemove(keyword);
              }}
            >
              <Trash2 className='h-4 w-4' />
              <span className='sr-only'>Remove {formatKeywordForDisplay(keyword.text)}</span>
            </Button>
          </div>
        )}
      </td>
    </tr>
  );
});

export function KeywordManager({
  keywords,
  setKeywords,
  adId,
  alternativeKeywords,
  setAlternativeKeywords: _setAlternativeKeywords,
  customKeyword: _customKeyword,
  setCustomKeyword,
  maxKeywordLength,
  disabled = false,
  hideAddButton = false,
  campaign,
  googleadsAdGroupId,
  onSaveAd,
}: KeywordManagerProps) {
  // Performance + Bidding columns only render if the campaign has ever been live (#4417).
  // Currently-LIVE and previously-live (paused) both have data worth showing; never-live
  // campaigns show all-zero rows that are pure visual noise. Same signal LiveSyncHelper uses.
  const hasBeenLive = !!campaign?.first_published_at;
  const isTestMode = useTestMode();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSuggestModalOpen, setIsSuggestModalOpen] = useState(false);
  const [selectedAlternatives, setSelectedAlternatives] = useState<Set<string>>(new Set());
  const [bulkKeywords, setBulkKeywords] = useState('');
  const [bulkMatchType, setBulkMatchType] = useState('PHRASE');
  // Tell-on-click validation (#5857): Add stays enabled (except at the keyword cap, which an
  // always-visible banner explains); clicking it marks the textarea inline instead of silently returning.
  const [bulkKeywordsError, setBulkKeywordsError] = useState<string | undefined>(undefined);
  // #4506: keyword detail sidebar disabled — state kept commented for re-enable.
  // const [selectedKeyword, setSelectedKeyword] = useState<Keyword | null>(null);
  // const [isMetricsSidebarOpen, setIsMetricsSidebarOpen] = useState(false);

  // Edit modal state
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingKeyword, setEditingKeyword] = useState<Keyword | null>(null);
  const [editText, setEditText] = useState('');
  // Tell-on-click validation (#5857): Save stays enabled; clicking it marks the empty field.
  const [editTextError, setEditTextError] = useState<string | undefined>(undefined);
  const [editMatchType, setEditMatchType] = useState('BROAD');
  // Google Ads owns the Final URL; the keyword row mirrors it after a successful sync. Fetched
  // live on open so the field shows what is actually serving, not a mirror that may predate a
  // change made in the Google Ads UI.
  const [editFinalUrl, setEditFinalUrl] = useState('');
  const [editFinalUrlOriginal, setEditFinalUrlOriginal] = useState('');
  // #5555: pending inline match-type change, awaiting the destructive-edit confirmation.
  const [pendingMatchType, setPendingMatchType] = useState<{ keyword: Keyword; matchType: string } | null>(null);
  const [isChangingMatchType, setIsChangingMatchType] = useState(false);
  // #4819: bulk match-type editing. Checkboxes are hidden until the user enters bulk mode, so the
  // table stays clean for normal use.
  const [bulkMode, setBulkMode] = useState(false);
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());
  // #4513: Refetch campaign after a destructive keyword mutation so local state
  // matches server (no auto-save reconciliation flap).
  const refetchCampaign = useRefetchCampaign(campaign?.slug ?? '');
  // Check if this is a competitor campaign

  // #5621: a keyword flagged is_deleted is soft-deleted — hidden from the list, counts and the dedup
  // set, but kept in `keywords` so the flag rides the next ad-save payload (and Deploy pauses it on
  // Google Ads). Re-adding the same text reuses the row server-side (revive).
  const visibleKeywords = useMemo(() => keywords.filter((k) => !k.is_deleted), [keywords]);

  const activeKeywordTexts = useMemo(
    () => new Set(visibleKeywords.map((k) => k.text.toLowerCase())),
    [visibleKeywords]
  );
  const availableAlternatives = useMemo(
    () => alternativeKeywords.filter((ak) => !activeKeywordTexts.has(ak.text.toLowerCase())),
    [alternativeKeywords, activeKeywordTexts]
  );

  // Batch fetch metrics for all keywords in a single API call
  const keywordIds = useMemo(() => keywords.filter(k => k.id).map(k => k.id!), [keywords]);
  const { data: batchMetrics, isLoading: batchMetricsLoading } = useBatchKeywordMetrics(keywordIds);

  // Create a map for quick lookup of metrics by keyword ID
  const metricsMap = useMemo(() => {
    if (!batchMetrics) return new Map<number, BatchKeywordMetrics>();
    return new Map(batchMetrics.map(m => [m.keyword_id, m]));
  }, [batchMetrics]);

  // #5555: cache the latest metrics keyed by keyword TEXT. Search volume / competition depend on the
  // text, not the match type — so when changing match type retire-and-recreates a keyword (new id,
  // same text), sorting + display can reuse the old numbers from this cache until the new id's
  // metrics load, instead of the keyword dropping to the bottom as "no data".
  const metricsByTextRef = useRef<Map<string, BatchKeywordMetrics>>(new Map());
  useEffect(() => {
    if (!batchMetrics) return;
    for (const k of keywords) {
      if (!k.id) continue;
      const m = metricsMap.get(k.id);
      if (m) metricsByTextRef.current.set(k.text.toLowerCase(), m);
    }
  }, [batchMetrics, keywords, metricsMap]);
  // Metrics for a keyword: by id, falling back to the text cache (covers a just-recreated keyword).
  const metricsFor = (k: Keyword): BatchKeywordMetrics | undefined =>
    (k.id ? metricsMap.get(k.id) : undefined) ?? metricsByTextRef.current.get(k.text.toLowerCase());

  // Batch fetch performance data for all keywords in a single API call
  const { data: batchPerformance, isLoading: batchPerformanceLoading } = useQuery<KeywordPerformanceData[]>({
    queryKey: ['keyword-performance-batch', campaign?.id],
    queryFn: () => {
      if (!campaign?.id) throw new Error('Campaign ID required');
      return fetchKeywordPerformanceBatch(campaign.id);
    },
    // Fetch whenever the campaign has ever been live — currently-LIVE and previously-live
    // (paused) both have meaningful performance history. Never-live campaigns skip the call.
    enabled: !!campaign?.id && hasBeenLive && keywordIds.length > 0,
    staleTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: false,
  });

  // Create a map for quick lookup of performance data by keyword text (lowercase).
  // Filter to rows for this ad's ad group first: the batch endpoint returns rows
  // for every ad group in the campaign, and the same keyword text can exist in
  // multiple ad groups. Without this filter, Map dedup by text keeps whichever
  // row arrives last (lowest impressions, given the ORDER BY), zeroing out real
  // metrics for keywords that also exist in sibling ad groups.
  const performanceMap = useMemo(() => {
    if (!batchPerformance) return new Map<string, KeywordPerformanceData>();
    const scoped = googleadsAdGroupId
      ? batchPerformance.filter(p => String(p.ad_group_id) === String(googleadsAdGroupId))
      : batchPerformance;
    return new Map(scoped.map(p => [p.keyword.toLowerCase(), p]));
  }, [batchPerformance, googleadsAdGroupId]);

  // Sort keywords using metrics from the batch map
  const keywordSortFns = useMemo(() => ({
    text: (a: Keyword, b: Keyword, dir: 'asc' | 'desc') => {
      const cmp = a.text.localeCompare(b.text);
      return dir === 'asc' ? cmp : -cmp;
    },
    avg_monthly_searches: (a: Keyword, b: Keyword, dir: 'asc' | 'desc') => {
      const aVal = ((a.id ? metricsMap.get(a.id) : undefined) ?? metricsByTextRef.current.get(a.text.toLowerCase()))?.avg_monthly_searches;
      const bVal = ((b.id ? metricsMap.get(b.id) : undefined) ?? metricsByTextRef.current.get(b.text.toLowerCase()))?.avg_monthly_searches;
      if (aVal == null && bVal == null) return 0;
      if (aVal == null) return 1;
      if (bVal == null) return -1;
      const cmp = (aVal as number) - (bVal as number);
      return dir === 'asc' ? cmp : -cmp;
    },
    competition: (a: Keyword, b: Keyword, dir: 'asc' | 'desc') => {
      const order: Record<string, number> = { LOW: 0, MEDIUM: 1, HIGH: 2 };
      const aVal = ((a.id ? metricsMap.get(a.id) : undefined) ?? metricsByTextRef.current.get(a.text.toLowerCase()))?.competition;
      const bVal = ((b.id ? metricsMap.get(b.id) : undefined) ?? metricsByTextRef.current.get(b.text.toLowerCase()))?.competition;
      if (aVal == null && bVal == null) return 0;
      if (aVal == null) return 1;
      if (bVal == null) return -1;
      const cmp = (order[aVal] ?? 3) - (order[bVal] ?? 3);
      return dir === 'asc' ? cmp : -cmp;
    },
    impressions: (a: Keyword, b: Keyword, dir: 'asc' | 'desc') => {
      const aVal = performanceMap.get(a.text.toLowerCase())?.impressions ?? 0;
      const bVal = performanceMap.get(b.text.toLowerCase())?.impressions ?? 0;
      const cmp = aVal - bVal;
      return dir === 'asc' ? cmp : -cmp;
    },
    quality_score: (a: Keyword, b: Keyword, dir: 'asc' | 'desc') => {
      const aVal = performanceMap.get(a.text.toLowerCase())?.quality_score ?? 0;
      const bVal = performanceMap.get(b.text.toLowerCase())?.quality_score ?? 0;
      return dir === 'asc' ? aVal - bVal : bVal - aVal;
    },
    impression_share: (a: Keyword, b: Keyword, dir: 'asc' | 'desc') => {
      const aVal = performanceMap.get(a.text.toLowerCase())?.search_impression_share ?? 0;
      const bVal = performanceMap.get(b.text.toLowerCase())?.search_impression_share ?? 0;
      const cmp = aVal - bVal;
      return dir === 'asc' ? cmp : -cmp;
    },
    match_type: (a: Keyword, b: Keyword, dir: 'asc' | 'desc') => {
      const cmp = (a.match_type || 'BROAD').localeCompare(b.match_type || 'BROAD');
      return dir === 'asc' ? cmp : -cmp;
    },
    is_paused: (a: Keyword, b: Keyword, dir: 'asc' | 'desc') => {
      // asc = active first (paused last); desc = paused first.
      const cmp = (a.is_paused ? 1 : 0) - (b.is_paused ? 1 : 0);
      return dir === 'asc' ? cmp : -cmp;
    },
    is_locked: (a: Keyword, b: Keyword, dir: 'asc' | 'desc') => {
      // asc = unlocked first; desc = locked first.
      const cmp = (a.is_locked ? 1 : 0) - (b.is_locked ? 1 : 0);
      return dir === 'asc' ? cmp : -cmp;
    },
  }), [metricsMap, performanceMap]);

  const { sortedData: sortedKeywords, handleSort: handleKeywordSort, getSortDirection: getKeywordSortDirection } = useTableSorting(
    visibleKeywords,
    { key: 'avg_monthly_searches', direction: 'desc' },
    keywordSortFns
  );

  const handleKeywordClick = (_keyword: Keyword) => {
    // #4506: keyword detail sidebar disabled; click is a no-op for now.
    // if (!keyword.id) {
    //   console.warn('Cannot show metrics for unsaved keyword:', keyword.text);
    //   return;
    // }
    // setSelectedKeyword(keyword);
    // setIsMetricsSidebarOpen(true);
  };

  // #4506: keyword detail sidebar disabled — handler kept commented for re-enable.
  // const handleCloseSidebar = () => {
  //   setIsMetricsSidebarOpen(false);
  //   setSelectedKeyword(null);
  // };

  const handleSuggestAdd = () => {
    const remaining = maxKeywordLength - visibleKeywords.length;
    const toAdd = availableAlternatives
      .filter((ak) => selectedAlternatives.has(ak.text))
      .slice(0, remaining)
      .map((ak) => ({ text: ak.text, match_type: ak.match_type || 'BROAD' }));
    setKeywords((prev) => [...prev, ...toAdd]);
    setSelectedAlternatives(new Set());
    setIsSuggestModalOpen(false);
  };

  const resetModal = () => {
    setBulkKeywords('');
    setBulkMatchType('PHRASE');
    setBulkKeywordsError(undefined);
    setIsModalOpen(false);
  };

  const handleAddKeywords = async () => {
    const keywordLines = bulkKeywords
      .split('\n')
      .map((line) => line.trim().toLowerCase())
      .filter((line) => line.length > 0);

    if (keywordLines.length === 0) {
      setBulkKeywordsError('Required');
      return;
    }

    const remaining = maxKeywordLength - visibleKeywords.filter(k => !k.is_paused).length;
    const valid = keywordLines
      .filter((text) => !visibleKeywords.some((k) => k.text === text && k.match_type === bulkMatchType))
      .slice(0, remaining);

    if (valid.length === 0) {
      // The live dupe banner above the button lists the skipped keywords.
      setBulkKeywordsError('All keywords already exist');
      return;
    }

    const newKeywords: Keyword[] = valid.map((text) => ({
      text,
      match_type: bulkMatchType,
    }));

    // Draft ad (no campaign/ad context): stay on the local-only path so the
    // existing launch flow can push them to Google Ads later.
    if (!campaign?.id || !adId) {
      setKeywords((prev) => [...prev, ...newKeywords]);
      setCustomKeyword('');
      resetModal();
      return;
    }

    // #4516: persisted ad → POST endpoint so Google Ads gets the keywords
    // immediately on live campaigns. Optimistic insert + revert on failure.
    const snapshot = keywords;
    setKeywords((prev) => [...prev, ...newKeywords]);
    setCustomKeyword('');
    resetModal();
    try {
      const result = await addKeywords(
        campaign.id,
        adId,
        newKeywords.map((k) => ({ text: k.text, match_type: k.match_type })),
      );
      // #4775: adopt the server-assigned ids onto the optimistic keywords. Without this the new
      // keyword stays id-less in adState; the next ad save's update_keywords reconciliation then
      // treats it as "not in the input" and pauses/removes it (matched by text + match_type, both
      // normalised upper-case server-side).
      setKeywords((prev) =>
        prev.map((k) => {
          if (k.id) return k;
          const match = result.keywords.find(
            (rk) => rk.text === k.text && rk.match_type?.toUpperCase() === k.match_type?.toUpperCase(),
          );
          return match ? { ...k, id: match.id } : k;
        }),
      );
      await refetchCampaign();
    } catch (err) {
      setKeywords(snapshot);
      toast({
        title: 'Could not add keywords',
        description: err instanceof Error ? err.message : 'Please try again.',
        variant: 'destructive',
      });
    }
  };

  // #4513 + #4626: persistent keyword → call the dedicated delete endpoint so
  // Google Ads stops serving it (with optimistic remove + revert on failure).
  // Unsaved keyword (no id, or temp negative id) → reference-identity filter
  // so we don't wipe every unsaved row that shares ``id=undefined``.
  const handleRemoveKeyword = (target: Keyword) => {
    const isUnsaved = !target.id || target.id < 0;
    if (isUnsaved) {
      // Never persisted — just drop it from local state.
      setKeywords((prev) => prev.filter((k) => k !== target));
      return;
    }
    // #5621: persisted keyword → stage a soft delete. Hidden from the list immediately and, on Save
    // Changes + Deploy, paused on Google Ads (kept + recoverable). The is_deleted flag rides the next
    // ad-save payload; nothing is pushed to Google Ads now (defer-to-Deploy).
    setKeywords((prev) => prev.map((k) => (k.id === target.id ? { ...k, is_deleted: true } : k)));
  };

  // #4522: optimistic update + revert + toast on failure, replacing the
  // previous fire-and-forget pattern that silently swallowed Google Ads sync
  // failures.
  const handleLockKeyword = async (keyword: Keyword, isLocked: boolean) => {
    if (!keyword.id) {
      // Unsaved keyword — no DB row to persist against. Local-only flip.
      setKeywords((prev) => prev.map((k) => (k === keyword ? { ...k, is_locked: isLocked } : k)));
      return;
    }
    const snapshot = keywords;
    setKeywords((prev) => prev.map((k) => (k.id === keyword.id ? { ...k, is_locked: isLocked } : k)));
    try {
      await lockKeyword(keyword.id, isLocked);
    } catch (err) {
      setKeywords(snapshot);
      toast({
        title: isLocked ? 'Could not lock keyword' : 'Could not unlock keyword',
        description: err instanceof Error ? err.message : 'Please try again.',
        variant: 'destructive',
      });
    }
  };

  const handlePauseKeyword = async (keyword: Keyword) => {
    if (!keyword.id || !campaign?.id || !adId) {
      setKeywords((prev) => prev.map((k) => (k.id === keyword.id ? { ...k, is_paused: true } : k)));
      return;
    }
    const snapshot = keywords;
    setKeywords((prev) => prev.map((k) => (k.id === keyword.id ? { ...k, is_paused: true } : k)));
    try {
      await pauseKeyword(campaign.id, adId, keyword.id);
    } catch (err) {
      setKeywords(snapshot);
      toast({
        title: 'Could not pause keyword',
        description: err instanceof Error ? err.message : 'Please try again.',
        variant: 'destructive',
      });
    }
  };

  const handleResumeKeyword = async (keyword: Keyword) => {
    if (!keyword.id || !campaign?.id || !adId) {
      setKeywords((prev) => prev.map((k) => (k.id === keyword.id ? { ...k, is_paused: false } : k)));
      return;
    }
    const snapshot = keywords;
    setKeywords((prev) => prev.map((k) => (k.id === keyword.id ? { ...k, is_paused: false } : k)));
    try {
      await resumeKeyword(campaign.id, adId, keyword.id);
    } catch (err) {
      setKeywords(snapshot);
      toast({
        title: 'Could not resume keyword',
        description: err instanceof Error ? err.message : 'Please try again.',
        variant: 'destructive',
      });
    }
  };

  const openEditModal = (keyword: Keyword) => {
    setEditingKeyword(keyword);
    setEditText(keyword.text);
    setEditTextError(undefined);
    setEditMatchType(keyword.match_type || 'BROAD');
    // Final URL is now carried on the keyword row (stored column), so pre-fill directly from it —
    // no per-open live fetch. The "only send if changed from original" rule in handleEditKeyword holds.
    setEditFinalUrl(keyword.final_url || '');
    setEditFinalUrlOriginal(keyword.final_url || '');
    setIsEditModalOpen(true);
  };

  const handleEditKeyword = async () => {
    if (!editingKeyword) return;
    if (!editText.trim()) {
      setEditTextError('Required');
      return;
    }
    const trimmed = editText.trim().toLowerCase();
    const targetKeyword = editingKeyword;
    // Only treat Final URL as edited if it differs from the value we pre-filled — a blank field left
    // over from a failed/absent read must not count as "cleared" and clobber the live URL.
    const finalUrlChanged = editFinalUrl.trim() !== editFinalUrlOriginal.trim();

    // If nothing changed, just close
    if (trimmed === targetKeyword.text && editMatchType === targetKeyword.match_type && !finalUrlChanged) {
      setIsEditModalOpen(false);
      return;
    }

    const isDuplicate = visibleKeywords.some(
      (k) => k !== editingKeyword && k.text === trimmed && k.match_type === editMatchType
    );
    if (isDuplicate) {
      // Tell-on-click (#5857): inline at the field instead of a toast.
      setEditTextError('Already exists on this ad');
      return;
    }

    // Unsaved keyword: local-only edit.
    if (!targetKeyword.id || !campaign?.id || !adId) {
      const updated = keywords.map((k) =>
        k === targetKeyword
          ? { ...k, text: trimmed, match_type: editMatchType, id: trimmed !== targetKeyword.text ? undefined : k.id }
          : k
      );
      setKeywords(updated);
      setIsEditModalOpen(false);
      onSaveAd?.(updated);
      return;
    }

    // #4515: the PATCH endpoint retire-and-recreates — it hard-deletes the old row and inserts a NEW
    // one with a new id. Adopt that returned id locally so adState matches the server. (The earlier
    // attempt set id=undefined, which never matched the server's new id, so the ad stayed dirty and a
    // later page save paused the new keyword as "missing". Using the real returned id fixes that: the
    // ad reads clean, the rehydrate re-syncs, and the edit shows without a manual refresh.)
    setIsEditModalOpen(false);
    try {
      const patch: { text: string; match_type: string; final_url?: string } = {
        text: trimmed,
        match_type: editMatchType,
      };
      // Send final_url only when the user actually changed it. Unchanged → omit → backend
      // leaves the criterion's Final URL untouched (and takes the in-place path when text/match
      // are also unchanged, so no history-losing retire-and-recreate happens for a URL-only edit).
      if (finalUrlChanged) patch.final_url = editFinalUrl.trim();
      const res = await updateKeyword(campaign.id, adId, targetKeyword.id, patch);
      // #5859: re-baseline from the server's recreated row rather than guessing. The backend
      // lowercases the text and owns is_paused/is_locked; any divergence between an optimistic
      // adoption and the refetched row flipped the ad dirty and stuck Save Changes on live campaigns.
      setKeywords((prev) =>
        prev.map((k) =>
          k.id === targetKeyword.id
            ? res.keyword
              ? { ...k, ...res.keyword, final_url: res.final_url ?? res.keyword.final_url ?? k.final_url }
              : { ...k, id: res.keyword_id, text: trimmed, match_type: editMatchType, is_paused: false, is_locked: false, final_url: res.final_url ?? k.final_url }
            : k,
        ),
      );
      await refetchCampaign();
    } catch (err) {
      toast({
        title: 'Could not update keyword',
        description: err instanceof Error ? err.message : 'Please try again.',
        variant: 'destructive',
      });
    }
  };

  // #5555: inline match-type change. Changing match type retire-and-recreates the keyword (losing
  // its performance history), so we confirm first, then commit via the same path the edit modal uses.
  const handleMatchTypeChange = (keyword: Keyword, matchType: string) => {
    if ((keyword.match_type || 'BROAD').toUpperCase() === matchType.toUpperCase()) return;
    const isDuplicate = visibleKeywords.some(
      (k) => k !== keyword && k.text === keyword.text && (k.match_type || 'BROAD').toUpperCase() === matchType.toUpperCase()
    );
    if (isDuplicate) {
      toast({
        description: `A keyword "${keyword.text}" with match type ${matchType.toLowerCase()} already exists in this ad group.`,
        variant: 'destructive',
      });
      return;
    }
    setPendingMatchType({ keyword, matchType });
  };

  const confirmMatchTypeChange = async () => {
    if (!pendingMatchType) return;
    const { keyword, matchType } = pendingMatchType;

    // Unsaved keyword (not yet on Google Ads): local-only — it'll be created with the new match type.
    if (!keyword.id || !campaign?.id || !adId) {
      // Match by id when present, else by text + match type (unique per ad group). Avoids relying on
      // object reference identity, which a background refetch could break while the modal is open.
      const matchesTarget = (k: Keyword) =>
        keyword.id
          ? k.id === keyword.id
          : k.text === keyword.text && (k.match_type || 'BROAD').toUpperCase() === (keyword.match_type || 'BROAD').toUpperCase();
      const updated = keywords.map((k) => (matchesTarget(k) ? { ...k, match_type: matchType } : k));
      setKeywords(updated);
      onSaveAd?.(updated);
      setPendingMatchType(null);
      return;
    }

    // Persisted: PATCH retire-and-recreate + refetch (same path as the edit modal). Keep the modal
    // open with a loading state while the request runs so the change is visibly in progress (#5624).
    setIsChangingMatchType(true);
    try {
      const res = await updateKeyword(campaign.id, adId, keyword.id, { text: keyword.text, match_type: matchType });
      // #5859: re-baseline from the server's recreated row (retire-and-recreate hard-deletes the
      // old row and creates a new one). Guessing here — old text casing, hardcoded
      // is_paused/is_locked — diverged from the refetched row on live campaigns (the backend
      // lowercases text; sync can pause/lock), which flipped the ad dirty and stuck Save Changes.
      setKeywords((prev) =>
        prev.map((k) =>
          k.id === keyword.id
            ? res.keyword
              ? { ...k, ...res.keyword }
              : { ...k, id: res.keyword_id, match_type: matchType, is_paused: false, is_locked: false }
            : k,
        ),
      );
      await refetchCampaign();
      setPendingMatchType(null);
    } catch (err) {
      toast({
        title: 'Could not update match type',
        description: err instanceof Error ? err.message : 'Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsChangingMatchType(false);
    }
  };

  // #4819 — bulk match-type editing helpers.
  const keyOf = (k: Keyword) => String(k.id ?? k.text);

  const exitBulkMode = () => {
    setBulkMode(false);
    setSelectedKeys(new Set());
  };

  const toggleSelect = (keyword: Keyword) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      const key = keyOf(keyword);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  const allSelected = sortedKeywords.length > 0 && sortedKeywords.every((k) => selectedKeys.has(keyOf(k)));
  const toggleSelectAll = () => {
    setSelectedKeys(allSelected ? new Set() : new Set(sortedKeywords.map(keyOf)));
  };

  const selectedKeywords = () => keywords.filter((k) => selectedKeys.has(keyOf(k)));

  // Bulk match-type / pause / lock are local edits (#4819): they update the ad's keyword state so the
  // existing top-bar Save Changes button + "Saved ✓" tick handle persistence and confirmation — no
  // immediate API call and no toast. Each skips no-ops (and set-match-type skips would-be duplicates).
  // Bulk mode stays open after each action so the user can chain several bulk edits back-to-back;
  // they leave it via the Done button. Each action mirrors its single-row counterpart so the result
  // is identical to doing them one at a time — no extra toast (the visible change is the feedback).
  const handleBulkSetMatchType = async (matchType: string) => {
    // Match type is a retire-and-recreate on Google Ads, so persisted keywords go through the
    // dedicated PATCH endpoint (NOT the ad save, which would pause the old keyword and create a
    // duplicate — #4819). Unsaved keywords just change locally and are created with the new type.
    const targets = selectedKeywords().filter(
      (k) =>
        (k.match_type || 'BROAD') !== matchType &&
        !visibleKeywords.some((o) => o !== k && o.text === k.text && (o.match_type || 'BROAD') === matchType)
    );
    if (targets.length === 0) return;
    const unsaved = targets.filter((k) => !(k.id && k.id > 0 && campaign?.id && adId));
    const persisted = targets.filter((k) => k.id && k.id > 0 && campaign?.id && adId);
    if (unsaved.length > 0) {
      const unsavedKeys = new Set(unsaved.map(keyOf));
      setKeywords((prev) => prev.map((k) => (unsavedKeys.has(keyOf(k)) ? { ...k, match_type: matchType } : k)));
    }
    if (persisted.length > 0) {
      try {
        const results = await Promise.all(
          persisted.map(async (k) => ({
            oldId: k.id!,
            keywordId: (await updateKeyword(campaign!.id, adId!, k.id!, { text: k.text, match_type: matchType })).keyword_id,
          })),
        );
        // Adopt each new server id so adState matches the server (retire-and-recreate); otherwise the
        // stale ids keep the ad dirty and the changes only show after a manual refresh.
        const idMap = new Map(results.map((r) => [r.oldId, r.keywordId]));
        setKeywords((prev) =>
          prev.map((k) => {
            const newId = k.id ? idMap.get(k.id) : undefined;
            return newId ? { ...k, id: newId, match_type: matchType, is_paused: false, is_locked: false } : k;
          }),
        );
        await refetchCampaign();
      } catch (err) {
        toast({ title: 'Could not update match types', description: err instanceof Error ? err.message : 'Please try again.', variant: 'destructive' });
      }
    }
  };

  const handleBulkPause = async () => {
    const targets = selectedKeywords().filter((k) => !k.is_paused);
    if (targets.length === 0) return;
    const unsaved = targets.filter((k) => !(k.id && campaign?.id && adId));
    const persisted = targets.filter((k) => k.id && campaign?.id && adId);
    if (unsaved.length > 0) {
      const keys = new Set(unsaved.map(keyOf));
      setKeywords((prev) => prev.map((k) => (keys.has(keyOf(k)) ? { ...k, is_paused: true } : k)));
    }
    if (persisted.length > 0) {
      try {
        await Promise.all(persisted.map((k) => pauseKeyword(campaign!.id, adId!, k.id!)));
        await refetchCampaign();
      } catch (err) {
        toast({ title: 'Could not pause keywords', description: err instanceof Error ? err.message : 'Please try again.', variant: 'destructive' });
      }
    }
  };

  const handleBulkLock = async () => {
    const targets = selectedKeywords().filter((k) => !k.is_locked);
    if (targets.length === 0) return;
    const unsaved = targets.filter((k) => !k.id);
    const persisted = targets.filter((k) => k.id);
    if (unsaved.length > 0) {
      const keys = new Set(unsaved.map(keyOf));
      setKeywords((prev) => prev.map((k) => (keys.has(keyOf(k)) ? { ...k, is_locked: true } : k)));
    }
    if (persisted.length > 0) {
      try {
        await Promise.all(persisted.map((k) => lockKeyword(k.id!, true)));
        await refetchCampaign();
      } catch (err) {
        toast({ title: 'Could not lock keywords', description: err instanceof Error ? err.message : 'Please try again.', variant: 'destructive' });
      }
    }
  };

  // #5621: deletes defer to Deploy. Unsaved rows drop from local state; persisted rows stage a soft
  // delete (is_deleted) that rides the next ad-save and is paused on Google Ads at Deploy — nothing
  // is pushed to Google Ads here. Stays in bulk mode; drops the removed rows from the selection.
  const handleBulkDelete = () => {
    const targets = selectedKeywords();
    if (targets.length === 0) return;
    const targetKeys = new Set(targets.map(keyOf));
    setSelectedKeys((prev) => new Set([...prev].filter((key) => !targetKeys.has(key))));
    const unsavedKeys = new Set(targets.filter((k) => !(k.id && k.id > 0)).map(keyOf));
    const persistedIds = new Set(targets.filter((k) => k.id && k.id > 0).map((k) => k.id));
    setKeywords((prev) =>
      prev
        .filter((k) => !unsavedKeys.has(keyOf(k)))
        .map((k) => (k.id && persistedIds.has(k.id) ? { ...k, is_deleted: true } : k)),
    );
  };

  // Virtualize the keyword table: only the visible window of rows is mounted, with
  // top/bottom spacer rows preserving scroll height and column layout (#5309).
  const tableScrollRef = useRef<HTMLDivElement>(null);
  const rowVirtualizer = useVirtualizer({
    count: sortedKeywords.length,
    getScrollElement: () => tableScrollRef.current,
    estimateSize: () => 60,
    overscan: 12,
  });
  const virtualItems = rowVirtualizer.getVirtualItems();
  const paddingTop = virtualItems.length ? virtualItems[0].start : 0;
  const paddingBottom = virtualItems.length
    ? rowVirtualizer.getTotalSize() - virtualItems[virtualItems.length - 1].end
    : 0;
  // Column count for the virtualization spacer rows: base 4 (Keyword/Match/Reach/Comp),
  // + 3 live-only (Performance/Bidding/QS), + 1 actions, + 1 bulk-select checkbox.
  const columnCount = (bulkMode ? 1 : 0) + 4 + (hasBeenLive ? 3 : 0) + 1;

  return (
    <div className='space-y-6'>

      <div>
        {/* Header */}
        <div className='flex items-center justify-between mb-4'>
          <div>
            <div className='flex items-center gap-2'>
              <h2 className='text-xl font-semibold text-gray-900'>Keywords</h2>
              <StringBadge string={visibleKeywords.filter(k => !k.is_paused).length + '/' + maxKeywordLength} size='sm' />
            </div>
            <p className='text-sm text-gray-500 mt-1'>Keywords you want this ad to show for.</p>
          </div>
          {!disabled && (
            bulkMode ? (
              // #4819: bulk-action bar — only shown while bulk-editing. A single Bulk actions menu
              // groups set-match-type (submenu), pause, lock and delete.
              <div className='flex items-center gap-2'>
                <span className='text-sm text-gray-600'>{selectedKeys.size} selected</span>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant='outline' className='text-gray-900' disabled={selectedKeys.size === 0}>
                      Bulk Actions
                      <ChevronDown className='h-4 w-4 ml-1.5 opacity-60' />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align='end' className='w-44'>
                    <DropdownMenuSub>
                      <DropdownMenuSubTrigger>Set match type</DropdownMenuSubTrigger>
                      <DropdownMenuSubContent className='w-64'>
                        <p className='text-xs text-[#A16207] bg-yellow-50 border border-yellow-200 rounded-md px-3 py-2 m-1'>
                          Match type can't be edited in place — Google Ads replaces the keyword with a new one, so its performance history won't carry over.
                        </p>
                        {['BROAD', 'PHRASE', 'EXACT'].map((mt) => (
                          <DropdownMenuItem key={mt} onClick={() => handleBulkSetMatchType(mt)}>
                            <span className={`inline-flex items-center px-3.5 py-0.5 rounded-full text-xs font-medium ${getMatchTypePillClasses(mt)}`}>
                              {matchTypeLabel(mt)}
                            </span>
                          </DropdownMenuItem>
                        ))}
                      </DropdownMenuSubContent>
                    </DropdownMenuSub>
                    <DropdownMenuItem onClick={handleBulkPause}>
                      <PauseCircle className='h-4 w-4 mr-2' />
                      Pause
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={handleBulkLock}>
                      <Lock className='h-4 w-4 mr-2' />
                      Lock
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem className='text-red-600 focus:text-red-600' onClick={handleBulkDelete}>
                      <Trash2 className='h-4 w-4 mr-2' />
                      Delete
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
                <Button variant='ghost' className='text-gray-500' onClick={exitBulkMode}>
                  Done
                </Button>
              </div>
            ) : (
              <div className='flex items-center gap-2'>
                {!hideAddButton && (
                  <Button
                    variant='outline'
                    className='text-gray-900'
                    onClick={() => { setBulkKeywordsError(undefined); setIsModalOpen(true); }}
                    disabled={visibleKeywords.filter(k => !k.is_paused).length >= maxKeywordLength}
                  >
                    <Plus className='h-4 w-4 mr-2' />
                    Add Keywords
                  </Button>
                )}
                {isTestMode && visibleKeywords.length > 1 && (
                  <Button variant='outline' className='text-gray-900' onClick={() => setBulkMode(true)}>
                    <ListTodo className='h-4 w-4 mr-2' />
                    Bulk Actions
                  </Button>
                )}
              </div>
            )
          )}
        </div>

        {/* Table following design system standards */}
        <div ref={tableScrollRef} className='-mx-6 -mb-6 max-h-[600px] overflow-auto rounded-b-lg'>
          {visibleKeywords.length > 0 ? (
              <table className='w-full divide-y divide-gray-200'>
                <thead className='sticky top-0 z-10 bg-white'>
                  <tr>
                    {bulkMode && (
                      <th className='pl-4 pr-0 py-2 w-10 text-left'>
                        <Checkbox checked={allSelected} onCheckedChange={toggleSelectAll} aria-label='Select all keywords' />
                      </th>
                    )}
                    <th className='pl-[26px] pr-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                      <SortableHeader sortDirection={getKeywordSortDirection('text')} onSort={() => handleKeywordSort('text')}>
                        Keyword
                      </SortableHeader>
                    </th>
                    <th className='px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-20'>
                      <SortableHeader sortDirection={getKeywordSortDirection('match_type')} onSort={() => handleKeywordSort('match_type')}>
                        Match
                      </SortableHeader>
                    </th>
                    <th className='px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider'>
                      <SortableHeader sortDirection={getKeywordSortDirection('avg_monthly_searches')} onSort={() => handleKeywordSort('avg_monthly_searches')}>
                        Reach
                      </SortableHeader>
                    </th>
                    <th className='px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider'>
                      <SortableHeader sortDirection={getKeywordSortDirection('competition')} onSort={() => handleKeywordSort('competition')}>
                        Comp.
                      </SortableHeader>
                    </th>
                    {hasBeenLive && (
                      <th className='px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                        <SortableHeader sortDirection={getKeywordSortDirection('impressions')} onSort={() => handleKeywordSort('impressions')}>
                          Performance
                        </SortableHeader>
                      </th>
                    )}
                    {hasBeenLive && (
                      <th className='px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                        <SortableHeader sortDirection={getKeywordSortDirection('impression_share')} onSort={() => handleKeywordSort('impression_share')}>
                          Bidding
                        </SortableHeader>
                      </th>
                    )}
                    {hasBeenLive && (
                      <th className='px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider'>
                        <SortableHeader sortDirection={getKeywordSortDirection('quality_score')} onSort={() => handleKeywordSort('quality_score')}>
                          QS
                        </SortableHeader>
                      </th>
                    )}
                    <th className='px-4 py-2'>
                      {(() => {
                        const pauseDir = getKeywordSortDirection('is_paused');
                        const PauseIcon = pauseDir === 'asc' ? ArrowUp : pauseDir === 'desc' ? ArrowDown : ArrowUpDown;
                        const lockDir = getKeywordSortDirection('is_locked');
                        const LockIcon = lockDir === 'asc' ? ArrowUp : lockDir === 'desc' ? ArrowDown : ArrowUpDown;
                        return (
                          <div className='flex justify-end gap-1'>
                            <button
                              type='button'
                              onClick={() => handleKeywordSort('is_paused')}
                              className='h-8 w-8 inline-flex items-center justify-center rounded hover:bg-gray-100 cursor-pointer'
                              title='Sort by status (paused/active)'
                              aria-label='Sort by status'
                            >
                              <PauseIcon className={`h-3.5 w-3.5 ${pauseDir ? 'text-gray-700' : 'text-gray-400'}`} />
                            </button>
                            <button
                              type='button'
                              onClick={() => handleKeywordSort('is_locked')}
                              className='h-8 w-8 inline-flex items-center justify-center rounded hover:bg-gray-100 cursor-pointer'
                              title='Sort by locked'
                              aria-label='Sort by locked'
                            >
                              <LockIcon className={`h-3.5 w-3.5 ${lockDir ? 'text-gray-700' : 'text-gray-400'}`} />
                            </button>
                            <span className='h-8 w-8' aria-hidden='true' />
                            <span className='h-8 w-8' aria-hidden='true' />
                          </div>
                        );
                      })()}
                    </th>
                  </tr>
                </thead>
                <tbody className='bg-white divide-y divide-gray-200'>
                  {paddingTop > 0 && (
                    <tr aria-hidden="true">
                      <td colSpan={columnCount} style={{ height: paddingTop, padding: 0, border: 0 }} />
                    </tr>
                  )}
                  {virtualItems.map((virtualRow) => {
                    const keyword = sortedKeywords[virtualRow.index];
                    return (
                      <KeywordTableRow
                        key={keyword.id || keyword.text}
                        ref={rowVirtualizer.measureElement}
                        dataIndex={virtualRow.index}
                        keyword={keyword}
                        disabled={disabled}
                        onRemove={handleRemoveKeyword}
                        onEdit={openEditModal}
                        onClick={handleKeywordClick}
                        onLock={handleLockKeyword}
                        onPause={handlePauseKeyword}
                        onResume={handleResumeKeyword}
                        bulkMode={bulkMode}
                        isSelected={selectedKeys.has(keyOf(keyword))}
                        onToggleSelect={toggleSelect}
                        metricsData={metricsFor(keyword)}
                        metricsLoading={batchMetricsLoading}
                        performanceData={performanceMap.get(keyword.text.toLowerCase())}
                        performanceLoading={batchPerformanceLoading}
                        hasBeenLive={hasBeenLive}
                        onMatchTypeChange={handleMatchTypeChange}
                      />
                    );
                  })}
                  {paddingBottom > 0 && (
                    <tr aria-hidden="true">
                      <td colSpan={columnCount} style={{ height: paddingBottom, padding: 0, border: 0 }} />
                    </tr>
                  )}
                </tbody>
              </table>
          ) : (
            <div className='flex flex-col items-center justify-center py-16 text-center'>
              <Search className='h-12 w-12 text-gray-500 mb-4' strokeWidth={1} />
              <h3 className='text-lg font-semibold text-gray-500 mb-2'>No Keywords</h3>
              <p className='text-gray-500 max-w-sm'>Add keywords to define which searches trigger this ad.</p>
            </div>
          )}
        </div>
        {/* Add Keywords Modal */}
        <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
          <DialogContent className='sm:max-w-[500px]' aria-describedby={undefined}>
            <DialogHeader>
              <DialogTitle>Add Keywords</DialogTitle>
            </DialogHeader>

            <div className='py-4 space-y-4'>
              {/* #5857: always-visible cap explainer — the Add button's only disabled term. */}
              {visibleKeywords.length >= maxKeywordLength && (
                <div className='flex items-start gap-2 px-3 py-2 rounded-lg border-l-4 bg-[#FEF3C7] border-[#FDE68A] text-[#A16207] text-sm'>
                  <AlertCircle className='h-4 w-4 mt-0.5 shrink-0' />
                  <span>{visibleKeywords.length} of {maxKeywordLength} keywords already added. Remove a keyword before adding more.</span>
                </div>
              )}
              <div className='space-y-2'>
                <div className='flex items-center justify-between'>
                  <Label>Keywords (one per line)</Label>
                  <FieldError message={bulkKeywordsError} />
                </div>
                <Textarea
                  value={bulkKeywords}
                  onChange={(e) => { setBulkKeywords(e.target.value); setBulkKeywordsError(undefined); }}
                  placeholder={'Enter keywords, one per line\nbest running shoes\nrunning shoes online\naffordable running shoes'}
                  className='min-h-[150px] text-gray-900'
                  error={!!bulkKeywordsError}
                />
              </div>
              {(() => {
                const lines = bulkKeywords
                  .split('\n')
                  .map((line) => line.trim().toLowerCase())
                  .filter((line) => line.length > 0);
                if (lines.length === 0) return null;
                const dupes = lines.filter((text) =>
                  visibleKeywords.some((k) => k.text === text && k.match_type === bulkMatchType)
                );
                const valid = lines.filter((text) =>
                  !visibleKeywords.some((k) => k.text === text && k.match_type === bulkMatchType)
                );
                const slots = Math.max(0, maxKeywordLength - visibleKeywords.length);
                const capped = valid.length > slots;
                return (
                  <div className='space-y-2'>
                    {/* slots === 0 is covered by the always-visible cap banner above. */}
                    {capped && slots > 0 && (
                      <div className='flex items-start gap-2 px-3 py-2 rounded-lg border-l-4 bg-[#FEF3C7] border-[#FDE68A] text-[#A16207] text-sm'>
                        <AlertCircle className='h-4 w-4 mt-0.5 shrink-0' />
                        <span>
                          {visibleKeywords.length} keyword{visibleKeywords.length === 1 ? '' : 's'} already added. Adding {valid.length} more exceeds the {maxKeywordLength} keyword limit, so only the first {slots} will be saved.
                        </span>
                      </div>
                    )}
                    {dupes.length > 0 && (
                      <div className='flex items-start gap-2 px-3 py-2 rounded-lg border-l-4 bg-[#FEF3C7] border-[#FDE68A] text-[#A16207] text-sm'>
                        <AlertCircle className='h-4 w-4 mt-0.5 shrink-0' />
                        <span>{dupes.length} keyword{dupes.length === 1 ? '' : 's'} skipped: {dupes.length === 1 ? 'it' : 'they'} already exist{dupes.length === 1 ? 's' : ''} with match type {bulkMatchType.toLowerCase()}.</span>
                      </div>
                    )}
                  </div>
                );
              })()}
              <div className='space-y-2'>
                <Label>Match Type</Label>
                <Select
                  value={bulkMatchType}
                  // Match type feeds the duplicate check, so changing it clears the inline error too.
                  onValueChange={(v) => { setBulkMatchType(v); setBulkKeywordsError(undefined); }}
                >
                  <SelectTrigger className='w-[200px]'>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value='BROAD'>Broad</SelectItem>
                    <SelectItem value='PHRASE'>Phrase</SelectItem>
                    <SelectItem value='EXACT'>Exact</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className='flex justify-end gap-2'>
              <Button variant='outline' onClick={resetModal}>
                Cancel
              </Button>
              <Button
                onClick={handleAddKeywords}
                // Tell-on-click (#5857): only the capacity cap disables Add (explained by the
                // always-visible banner); empty/all-duplicate input surfaces inline on click.
                disabled={visibleKeywords.length >= maxKeywordLength}
                className='bg-[#290BD9] hover:bg-[#2309C5]'
              >
                Add
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Suggest Keywords Modal */}
        <Dialog open={isSuggestModalOpen} onOpenChange={(open) => { setIsSuggestModalOpen(open); if (!open) setSelectedAlternatives(new Set()); }}>
          <DialogContent className='sm:max-w-[500px]' aria-describedby={undefined}>
            <DialogHeader>
              <DialogTitle>Suggest Keywords</DialogTitle>
            </DialogHeader>
            <div className='py-4 space-y-3'>
              {availableAlternatives.length === 0 ? (
                <p className='text-sm text-gray-500'>No alternative keywords available.</p>
              ) : (
                <>
                  <p className='text-sm text-gray-500'>Select keywords to add to this ad group.</p>
                  <div className='max-h-[320px] overflow-y-auto space-y-2 pr-1'>
                    {availableAlternatives.map((ak) => (
                      <label key={ak.text} className='flex items-center gap-3 px-3 py-2 rounded-md hover:bg-gray-50 cursor-pointer'>
                        <Checkbox
                          checked={selectedAlternatives.has(ak.text)}
                          onCheckedChange={(checked) => {
                            setSelectedAlternatives((prev) => {
                              const next = new Set(prev);
                              checked ? next.add(ak.text) : next.delete(ak.text);
                              return next;
                            });
                          }}
                        />
                        <span className='text-sm text-gray-900 flex-1'>{ak.text}</span>
                        <span className='text-xs text-gray-400'>{(ak.match_type || 'BROAD').charAt(0) + (ak.match_type || 'BROAD').slice(1).toLowerCase()}</span>
                      </label>
                    ))}
                  </div>
                  {(() => {
                    const selCount = selectedAlternatives.size;
                    const remaining = maxKeywordLength - visibleKeywords.length;
                    if (selCount === 0 || selCount <= remaining) return null;
                    return (
                      <div className='flex items-start gap-2 px-3 py-2 rounded-lg border-l-4 bg-[#FEF3C7] border-[#FDE68A] text-[#A16207] text-sm'>
                        <AlertCircle className='h-4 w-4 mt-0.5 shrink-0' />
                        <span>Only {remaining} keyword{remaining === 1 ? '' : 's'} can be added (limit is {maxKeywordLength}). The first {remaining} selected will be added.</span>
                      </div>
                    );
                  })()}
                </>
              )}
            </div>
            <div className='flex justify-end gap-2'>
              <Button variant='outline' onClick={() => { setIsSuggestModalOpen(false); setSelectedAlternatives(new Set()); }}>
                Cancel
              </Button>
              <Button
                onClick={handleSuggestAdd}
                disabled={selectedAlternatives.size === 0}
                className='bg-[#290BD9] hover:bg-[#2309C5]'
              >
                Add Selected
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Edit Keyword Modal */}
        <Dialog open={isEditModalOpen} onOpenChange={setIsEditModalOpen}>
          <DialogContent className='sm:max-w-[400px]' aria-describedby={undefined}>
            <DialogHeader>
              <DialogTitle>Edit Keyword</DialogTitle>
            </DialogHeader>
            <div className='py-4 space-y-4'>
              <div className='space-y-2'>
                <div className='flex items-center justify-between'>
                  <Label>Keyword</Label>
                  <FieldError message={editTextError} />
                </div>
                <div className='relative'>
                  <input
                    type='text'
                    value={editText}
                    onChange={(e) => { setEditText(e.target.value); setEditTextError(undefined); }}
                    className={`flex h-10 w-full rounded-md border bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 ${editTextError ? 'border-red-500 pr-8 focus-visible:ring-red-500' : 'border-input focus-visible:ring-ring'}`}
                    autoFocus
                  />
                  <InputAdornment error={!!editTextError} />
                </div>
              </div>
              <div className='space-y-2'>
                <Label>Match Type</Label>
                {/* Match type feeds the duplicate check, so changing it clears the inline error too. */}
                <Select value={editMatchType} onValueChange={(v) => { setEditMatchType(v); setEditTextError(undefined); }}>
                  <SelectTrigger className='w-[200px]'>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value='BROAD'>Broad</SelectItem>
                    <SelectItem value='PHRASE'>Phrase</SelectItem>
                    <SelectItem value='EXACT'>Exact</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {editingKeyword?.id && campaign?.id && adId ? (
                <div className='space-y-2'>
                  <Label>Final URL</Label>
                  <input
                    type='url'
                    inputMode='url'
                    placeholder='https://example.com/landing-page'
                    value={editFinalUrl}
                    onChange={(e) => setEditFinalUrl(e.target.value)}
                    className='flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
                  />
                  <p className='text-xs text-gray-400'>
                    The landing page this keyword points to. Saved to Google Ads in place, so history is kept. Leave blank to clear it.
                  </p>
                </div>
              ) : null}
              {editingKeyword && editText.trim().toLowerCase() !== editingKeyword.text && (
                <p className='text-xs text-[#A16207] bg-yellow-50 border border-yellow-200 rounded-md px-3 py-2'>
                  Changing keyword text will create a new keyword in Google Ads. Performance history from the original keyword will not carry over.
                </p>
              )}
            </div>
            <div className='flex justify-end gap-2'>
              <Button variant='outline' onClick={() => setIsEditModalOpen(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleEditKeyword}
                className='bg-[#290BD9] hover:bg-[#2309C5]'
              >
                Save
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* #5555: confirm the destructive inline match-type change (retire-and-recreate). */}
        <ConformationModal
          isOpen={!!pendingMatchType}
          onClose={() => setPendingMatchType(null)}
          onConfirm={confirmMatchTypeChange}
          variant="destructive"
          title="Change match type?"
          description={pendingMatchType ? `Changing "${pendingMatchType.keyword.text}" to ${matchTypeLabel(pendingMatchType.matchType)} match replaces the keyword in Google Ads. Its performance history won't carry over.` : ''}
          confirmText="Change match type"
          isLoading={isChangingMatchType}
          loadingText="Changing..."
        />
      </div>

      {/* Alternative Keywords - commented out for future use
      <div className='space-y-4'>
        <h2 className='text-gray-900 font-semibold mb-2'>Alternative Keywords</h2>
        <div>
          {alternativeKeywords.length === 0 ? (
            <p className='text-gray-500 text-sm'>No alternative keywords available</p>
          ) : (
            <>
              <table className='w-full'>
                <tbody>
                  {(showAllAlternatives ? alternativeKeywords : alternativeKeywords.slice(0, 4)).map((keyword) =>
                    renderKeywordRow(keyword, 'alternative'),
                  )}
                </tbody>
              </table>
              {!showAllAlternatives && alternativeKeywords.length > 4 && (
                <Button
                  variant='link'
                  className='text-sm text-gray-500 mt-2 p-0 h-auto'
                  onClick={(e) => {
                    e.preventDefault();
                    setShowAllAlternatives(true);
                  }}
                >
                  +{alternativeKeywords.length - 4} more
                </Button>
              )}
              {showAllAlternatives && alternativeKeywords.length > 4 && (
                <Button
                  variant='link'
                  className='text-sm text-gray-500 mt-2 p-0 h-auto'
                  onClick={(e) => {
                    e.preventDefault();
                    setShowAllAlternatives(false);
                  }}
                >
                  Show less
                </Button>
              )}
            </>
          )}
        </div>
      </div>
      */}
      {/* #4506: keyword detail sidebar disabled — render commented out, kept for re-enable. */}
      {/* {selectedKeyword && (
        <KeywordMetricsSidebar
          keyword={selectedKeyword}
          isOpen={isMetricsSidebarOpen}
          onClose={handleCloseSidebar}
          campaignId={campaign?.id}
          googleadsAdGroupId={googleadsAdGroupId}
        />
      )} */}
    </div>
  );
}
